/**
 * Phase 3: TypeScript 타입 시스템 통합
 * 
 * trip_status enum을 위한 타입 안전한 헬퍼 함수들
 * 
 * Database enum values:
 * - open: 모집시작 (새로운 투어 모집 시작)
 * - pending: 모집중 (최소인원 미달 대기)
 * - confirmed: 출발 확정
 * - ready: 출발 준비 완료
 * - departed: 출발 완료
 * - in_progress: 여행 진행 중
 * - completed: 여행 완료
 * - cancelled: 여행 취소
 * - delayed: 출발 지연
 * - emergency: 응급 상황
 */

import { Database } from "@/integrations/supabase/types";
import { 
  PlayCircle, 
  Clock, 
  CheckCircle, 
  Rocket, 
  Navigation, 
  Loader, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  AlertOctagon,
  type LucideIcon 
} from "lucide-react";
import { withRoEuro } from "./korean-utils";

// 편의성을 위한 타입 별칭
export type TripStatus = Database["public"]["Enums"]["trip_status"];

// 상수 정의 - Database에서 자동 추출된 값과 일치 보장
export const TRIP_STATUS = {
  OPEN: "open" as TripStatus,
  PENDING: "pending" as TripStatus,
  CONFIRMED: "confirmed" as TripStatus,
  READY: "ready" as TripStatus,
  DEPARTED: "departed" as TripStatus,
  IN_PROGRESS: "in_progress" as TripStatus,
  COMPLETED: "completed" as TripStatus,
  CANCELLED: "cancelled" as TripStatus,
  DELAYED: "delayed" as TripStatus,
  EMERGENCY: "emergency" as TripStatus,
} as const;

// 유효한 상태값들의 배열 (컴파일 타임에 검증)
export const TRIP_STATUS_VALUES: TripStatus[] = [
  TRIP_STATUS.OPEN,
  TRIP_STATUS.PENDING,
  TRIP_STATUS.CONFIRMED,
  TRIP_STATUS.READY,
  TRIP_STATUS.DEPARTED,
  TRIP_STATUS.IN_PROGRESS,
  TRIP_STATUS.COMPLETED,
  TRIP_STATUS.CANCELLED,
  TRIP_STATUS.DELAYED,
  TRIP_STATUS.EMERGENCY,
] as const;

/**
 * 주어진 문자열이 유효한 trip_status인지 확인
 */
export function isValidTripStatus(status: string): status is TripStatus {
  return TRIP_STATUS_VALUES.includes(status as TripStatus);
}

/**
 * 안전한 상태 변환 - 유효하지 않은 값에 대해서는 default 반환
 */
export function parseTripStatus(
  status: string | null | undefined,
  defaultStatus: TripStatus = TRIP_STATUS.OPEN
): TripStatus {
  if (!status) return defaultStatus;
  return isValidTripStatus(status) ? status : defaultStatus;
}

/**
 * 상태 전이 유효성 검사
 */
export function isValidTripStatusTransition(
  currentStatus: TripStatus,
  newStatus: TripStatus
): boolean {
  const validTransitions: Record<TripStatus, TripStatus[]> = {
    [TRIP_STATUS.OPEN]: [
      TRIP_STATUS.PENDING,
      TRIP_STATUS.CONFIRMED,
      TRIP_STATUS.CANCELLED,
    ],
    [TRIP_STATUS.PENDING]: [
      TRIP_STATUS.CONFIRMED,
      TRIP_STATUS.CANCELLED,
    ],
    [TRIP_STATUS.CONFIRMED]: [
      TRIP_STATUS.READY,
      TRIP_STATUS.DELAYED,
      TRIP_STATUS.CANCELLED,
    ],
    [TRIP_STATUS.READY]: [
      TRIP_STATUS.DEPARTED,
      TRIP_STATUS.DELAYED,
      TRIP_STATUS.EMERGENCY,
    ],
    [TRIP_STATUS.DEPARTED]: [
      TRIP_STATUS.IN_PROGRESS,
      TRIP_STATUS.EMERGENCY,
    ],
    [TRIP_STATUS.IN_PROGRESS]: [
      TRIP_STATUS.COMPLETED,
      TRIP_STATUS.EMERGENCY,
    ],
    [TRIP_STATUS.DELAYED]: [
      TRIP_STATUS.READY,
      TRIP_STATUS.CANCELLED,
      TRIP_STATUS.EMERGENCY,
    ],
    [TRIP_STATUS.EMERGENCY]: [
      TRIP_STATUS.IN_PROGRESS,
      TRIP_STATUS.COMPLETED,
      TRIP_STATUS.CANCELLED,
    ],
    [TRIP_STATUS.COMPLETED]: [], // 완료 후에는 상태 변경 불가
    [TRIP_STATUS.CANCELLED]: [], // 취소 후에는 상태 변경 불가
  };

  return validTransitions[currentStatus]?.includes(newStatus) ?? false;
}

/**
 * 상태별 한국어 라벨
 */
export function getTripStatusLabel(status: TripStatus): string {
  const labels: Record<TripStatus, string> = {
    [TRIP_STATUS.OPEN]: "모집시작",
    [TRIP_STATUS.PENDING]: "모집중",
    [TRIP_STATUS.CONFIRMED]: "출발확정",
    [TRIP_STATUS.READY]: "출발 준비 완료",
    [TRIP_STATUS.DEPARTED]: "출발함",
    [TRIP_STATUS.IN_PROGRESS]: "진행중",
    [TRIP_STATUS.COMPLETED]: "완료",
    [TRIP_STATUS.CANCELLED]: "취소",
    [TRIP_STATUS.DELAYED]: "지연",
    [TRIP_STATUS.EMERGENCY]: "긴급",
  };

  return labels[status];
}

/**
 * 상태별 한국어 라벨에 "로/으로 변경" 조사를 붙인 텍스트 반환
 * 예: "모집중으로 변경", "완료로 변경"
 */
export function getTripStatusLabelWithJosa(status: TripStatus, suffix: string = "변경"): string {
  const label = getTripStatusLabel(status);
  return `${withRoEuro(label)} ${suffix}`;
}

/**
 * 상태별 아이콘
 */
export function getTripStatusIcon(status: TripStatus): LucideIcon {
  const icons: Record<TripStatus, LucideIcon> = {
    [TRIP_STATUS.OPEN]: PlayCircle,
    [TRIP_STATUS.PENDING]: Clock,
    [TRIP_STATUS.CONFIRMED]: CheckCircle,
    [TRIP_STATUS.READY]: Rocket,  // 로켓 아이콘 사용
    [TRIP_STATUS.DEPARTED]: Navigation,
    [TRIP_STATUS.IN_PROGRESS]: Loader,
    [TRIP_STATUS.COMPLETED]: CheckCircle2,
    [TRIP_STATUS.CANCELLED]: XCircle,
    [TRIP_STATUS.DELAYED]: AlertTriangle,
    [TRIP_STATUS.EMERGENCY]: AlertOctagon,
  };

  return icons[status];
}

/**
 * 상태별 색상 클래스 (Tailwind CSS)
 */
export function getTripStatusColor(status: TripStatus): {
  bg: string;
  text: string;
  border: string;
} {
  const colors: Record<TripStatus, { bg: string; text: string; border: string }> = {
    [TRIP_STATUS.OPEN]: {
      bg: "bg-blue-50",
      text: "text-blue-700",
      border: "border-blue-200",
    },
    [TRIP_STATUS.PENDING]: {
      bg: "bg-yellow-50",
      text: "text-yellow-700",
      border: "border-yellow-200",
    },
    [TRIP_STATUS.CONFIRMED]: {
      bg: "bg-green-50",
      text: "text-green-700",
      border: "border-green-200",
    },
    [TRIP_STATUS.READY]: {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      border: "border-emerald-200",
    },
    [TRIP_STATUS.DEPARTED]: {
      bg: "bg-indigo-50",
      text: "text-indigo-700",
      border: "border-indigo-200",
    },
    [TRIP_STATUS.IN_PROGRESS]: {
      bg: "bg-purple-50",
      text: "text-purple-700",
      border: "border-purple-200",
    },
    [TRIP_STATUS.COMPLETED]: {
      bg: "bg-gray-50",
      text: "text-gray-700",
      border: "border-gray-200",
    },
    [TRIP_STATUS.CANCELLED]: {
      bg: "bg-red-50",
      text: "text-red-700",
      border: "border-red-200",
    },
    [TRIP_STATUS.DELAYED]: {
      bg: "bg-orange-50",
      text: "text-orange-700",
      border: "border-orange-200",
    },
    [TRIP_STATUS.EMERGENCY]: {
      bg: "bg-red-100",
      text: "text-red-800",
      border: "border-red-300",
    },
  };

  return colors[status];
}

/**
 * 예약 가능한 상태인지 확인
 * OPEN(모집시작), PENDING(모집중), CONFIRMED(출발확정) 상태에서 예약 가능
 */
export function isBookableTrip(status: TripStatus): boolean {
  return [
    TRIP_STATUS.OPEN,      // 모집시작 - 새로운 투어 모집 시작
    TRIP_STATUS.PENDING,   // 모집중 - 최소인원 미달이지만 계속 예약 받는 중
    TRIP_STATUS.CONFIRMED  // 출발확정 - 좌석이 남아있다면 추가 예약 가능
  ].includes(status);
}

/**
 * 취소 가능한 상태인지 확인
 */
export function canCancelTrip(status: TripStatus): boolean {
  return [
    TRIP_STATUS.OPEN,
    TRIP_STATUS.PENDING,
    TRIP_STATUS.CONFIRMED,
    TRIP_STATUS.DELAYED,
  ].includes(status);
}

/**
 * 완료된 상태인지 확인 (완료 또는 취소)
 */
export function isCompletedTrip(status: TripStatus): boolean {
  return status === TRIP_STATUS.COMPLETED || status === TRIP_STATUS.CANCELLED;
}

/**
 * 진행 중인 상태인지 확인
 */
export function isActiveTrip(status: TripStatus): boolean {
  return [
    TRIP_STATUS.READY,
    TRIP_STATUS.DEPARTED,
    TRIP_STATUS.IN_PROGRESS,
    TRIP_STATUS.DELAYED,
    TRIP_STATUS.EMERGENCY,
  ].includes(status);
}

/**
 * 응급 상황인지 확인
 */
export function isEmergencyTrip(status: TripStatus): boolean {
  return status === TRIP_STATUS.EMERGENCY;
}

/**
 * 관리자용 상태 변경 헬퍼
 */
export function getAvailableTripStatusTransitions(
  currentStatus: TripStatus
): { value: TripStatus; label: string; priority?: "high" | "normal" | "low" }[] {
  const validNextStatuses = TRIP_STATUS_VALUES.filter(status =>
    isValidTripStatusTransition(currentStatus, status)
  );

  return validNextStatuses.map(status => ({
    value: status,
    label: getTripStatusLabel(status),
    priority: status === TRIP_STATUS.EMERGENCY ? "high" :
             status === TRIP_STATUS.CANCELLED ? "normal" : "low",
  }));
}

/**
 * 상태별 우선순위 (정렬용)
 */
export function getTripStatusPriority(status: TripStatus): number {
  const priorities: Record<TripStatus, number> = {
    [TRIP_STATUS.EMERGENCY]: 100,    // 최고 우선순위
    [TRIP_STATUS.DELAYED]: 90,
    [TRIP_STATUS.IN_PROGRESS]: 80,
    [TRIP_STATUS.DEPARTED]: 70,
    [TRIP_STATUS.READY]: 60,
    [TRIP_STATUS.CONFIRMED]: 50,
    [TRIP_STATUS.PENDING]: 40,
    [TRIP_STATUS.OPEN]: 30,
    [TRIP_STATUS.COMPLETED]: 20,
    [TRIP_STATUS.CANCELLED]: 10,     // 최저 우선순위
  };

  return priorities[status];
}

/**
 * 타입 가드: Database 타입과 일치하는지 확인
 */
export function assertTripStatus(status: unknown): asserts status is TripStatus {
  if (typeof status !== "string" || !isValidTripStatus(status)) {
    throw new Error(`Invalid trip status: ${status}`);
  }
}

/**
 * Badge variant 매핑 (shadcn/ui Badge 컴포넌트용)
 */
export function getTripStatusVariant(status: TripStatus): "default" | "secondary" | "destructive" | "outline" {
  const variants: Record<TripStatus, "default" | "secondary" | "destructive" | "outline"> = {
    [TRIP_STATUS.OPEN]: "outline",
    [TRIP_STATUS.PENDING]: "secondary", 
    [TRIP_STATUS.CONFIRMED]: "default",
    [TRIP_STATUS.READY]: "default",
    [TRIP_STATUS.DEPARTED]: "default",
    [TRIP_STATUS.IN_PROGRESS]: "default",
    [TRIP_STATUS.COMPLETED]: "secondary",
    [TRIP_STATUS.CANCELLED]: "destructive",
    [TRIP_STATUS.DELAYED]: "destructive",
    [TRIP_STATUS.EMERGENCY]: "destructive",
  };

  return variants[status];
}

/**
 * 좌석과 시간을 고려한 예약 가능 여부 확인
 * 상태, 남은 좌석, 출발 시간 등을 종합적으로 판단
 */
export function canBookTrip(
  status: TripStatus,
  availableSeats: number,
  departureDate: string,
  cutoffHours: number = 24 // 기본값: 출발 24시간 전 마감
): boolean {
  // 1. 상태 체크
  if (!isBookableTrip(status)) {
    return false;
  }
  
  // 2. 좌석 체크
  if (availableSeats <= 0) {
    return false;
  }
  
  // 3. 시간 체크
  const departure = new Date(departureDate);
  const cutoff = new Date();
  cutoff.setHours(cutoff.getHours() + cutoffHours);
  
  if (departure <= cutoff) {
    return false; // 출발 시간이 너무 임박함
  }
  
  return true;
}

/**
 * Trip 생성 가능 여부 확인
 * 동일한 product_id와 departure_date의 trip이 이미 존재하는지 체크하기 위한 헬퍼
 */
export function getReusableTripStatuses(): TripStatus[] {
  // 재사용 가능한 trip 상태들
  return [
    TRIP_STATUS.OPEN,
    TRIP_STATUS.PENDING,
    TRIP_STATUS.CONFIRMED
  ];
}

/**
 * 개발용 유틸리티: 모든 enum 값이 처리되었는지 컴파일 타임에 확인
 */
export function exhaustiveTripStatusCheck(status: never): never {
  throw new Error(`Unhandled trip status: ${status}`);
}